import {Action, createReducer, on} from '@ngrx/store';

import {CardDetails} from 'sp-mark/models/card-details.interface';
import * as paymentConclusion from 'sp-mark/redux/payment-conclusion/payment-conclusion.actions';
import {calculateLastRateValue, calculateRateValue, dateMonthDifference, roundNumber, SellPreview} from 'sp-ui-lib';

export interface PaymentConclusionState {
  operationInfo: SellPreview;
  flagUnavailableOperation: boolean;
  flagLoadingContent: boolean;
  flagLoadingModuleContent: boolean;
  cardDetails: CardDetails;
  actionRequired: boolean;
  paymentKoMessage: string;
  totalInstallments: number;
}

const initialState: PaymentConclusionState = {
  operationInfo: null,
  flagUnavailableOperation: false,
  flagLoadingContent: true,
  flagLoadingModuleContent: true,
  cardDetails: null,
  actionRequired: null,
  paymentKoMessage: '',
  totalInstallments: null
};

const paymentConclusionReducer = createReducer(
  initialState,
  on(paymentConclusion.retrieveSellFailure, (state, props) => ({
    ...state,
    flagUnavailableOperation: true,
    flagLoadingContent: false
  })),
  on(paymentConclusion.retrieveSellSuccess, (state, props) => ({
    ...state,
    operationInfo: props.operation,
    flagUnavailableOperation: false,
    flagLoadingContent: false
  })),
  on(paymentConclusion.executePaymentKo, (state, props) => ({
    ...state,
    paymentKoMessage: props.sellResponse.message
  })),
  on(paymentConclusion.resetOperationAvailability, (state, props) => ({
    ...state,
    flagUnavailableOperation: false,
    flagLoadingContent: true
  })),
  on(paymentConclusion.setLoadingContent, (state, props) => ({
    ...state,
    flagLoadingModuleContent: props.spinner
  })),
  on(paymentConclusion.storeCardDetails, (state, props) => ({
    ...state,
    cardDetails: props.cardDetails
  })),
  on(paymentConclusion.reorganizeInstallments, (state, props) => {
    const firstInstallmentValue = +state.operationInfo.installments[0].amount;
    let installmentsValue = state.operationInfo.installments.reduce((acc, val) => +acc + roundNumber(+val.amount), 0);
    installmentsValue = installmentsValue - firstInstallmentValue;
    const today = new Date();
    const expiryDate = new Date();
    expiryDate.setFullYear(props.expiryYear);
    expiryDate.setMonth(props.expiryMonth);
    const monthDifference = dateMonthDifference(today, expiryDate) - 2;
    const installments = [];
    for (let i = 0; i <= monthDifference; i++) {
      const paymentDate = new Date();
      const currentRateValue =
        i === monthDifference
          ? calculateLastRateValue(monthDifference, installmentsValue)
          : calculateRateValue(monthDifference, installmentsValue);

      paymentDate.setMonth(paymentDate.getMonth() + i);
      installments.push({
        amount: i === 0 ? firstInstallmentValue : currentRateValue,
        payableBy: paymentDate,
        id: i + 1
      });
    }

    return {
      ...state,
      operationInfo: {
        ...state.operationInfo,
        installments
      }
    };
  }),
  on(paymentConclusion.actionRequiredOnPayment, (state, props) => ({
    ...state,
    actionRequired: props.actionRequired
  })),
  on(paymentConclusion.updateTotalInstallmentsAction, (state, {totalInstallments}) => ({
    ...state,
    operationInfo: {
      ...state.operationInfo,
      amount: state.operationInfo.multipleInstallments ? state.operationInfo.multipleInstallments[`${totalInstallments}`][0].amount : state.operationInfo.amount
    },
    totalInstallments
  }))
);

export function reducer(state: PaymentConclusionState | undefined, action: Action) {
  return paymentConclusionReducer(state, action);
}
